import React, { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import Text from '../../../../../atoms/text/Text'
import VerticalGroup from '../../../../../atoms/verticalgroup/VerticalGroup'
import Dropdown from '../../../../../atoms/form/input/Dropdown'
import InlineGroup from '../../../../../atoms/inlinegroup/InlineGroup'
import IconButton from '../../../../../molecules/iconbutton/IconButton'
import Button from '../../../../../atoms/button/Button'
import useAssessments from '../../../../../../hooks/useAssessments'
import AssessmentPreviewModal from './AssessmentPreviewModal'
// import SimulationPreviewModal from './SimulationPreviewModal'

interface SelectValue {
  id: string
  value: string
  label: string
  assessment: API.AssessmentResponse
}

interface SelectAssessmentProps {
  assessmentId?: string
  clientId?: string
  readOnly?: boolean
  state?: string
  onAssessmentSelected?: (assessment: API.AssessmentResponse) => void
  onUnselectAssessment?: () => void
}

const SelectAssessment: React.FC<SelectAssessmentProps> = ({
  assessmentId,
  clientId,
  readOnly,
  onAssessmentSelected,
  onUnselectAssessment,
}) => {
  const { eventId } = useParams<{ eventId: string }>()
  const [showSettingsPreviewModal, setShowSettingsPreviewModal] = useState<boolean>(false)
  const { data } = useAssessments(clientId ? [clientId] : [])

  const options: SelectValue[] = useMemo(() => {
    return data ? data.map(assessment => ({
      id: assessment.id,
      value: assessment.id,
      label: assessment.name,
      assessment,
    })) : []
  }, [data])

  const currentSelected = useMemo(() => {
    if (assessmentId) {
      return options.find(option => option.id === assessmentId)
    }
    return null
  }, [assessmentId, options])


  const status = useMemo(() => {
    if (readOnly) {
      if (!!currentSelected) {
        return 'readOnlyWithAssessment'
      }
      return 'readOnlyWithoutAssessment'
    }

    return 'default'
  }, [currentSelected, readOnly])

  const content = useMemo(() => {
    switch (status) {
      case 'readOnlyWithAssessment':
        return (
          <>
            <InlineGroup spaceLeft={2}>
              <Text size="sm">Assessment Selected: </Text>
              <Text size="sm" bold >{currentSelected?.label}</Text>
            </InlineGroup>
            <Button link disabled={!!currentSelected}>
              <Link to={`/events/${eventId}/view/assessments/${currentSelected?.id}`}>Mark assessments</Link>
            </Button>
          </>
        )
      case 'readOnlyWithoutAssessment':
        return (
          <InlineGroup spaceLeft={2}>
            <Text size="sm" colour='danger' bold>No Assessment Selected</Text>
          </InlineGroup>
        )
      case 'default':
        return (
          <InlineGroup spaceLeft={4}>
            <Dropdown
              selectProps={{
                isDisabled: !clientId,
                onChange: (option: SelectValue) => onAssessmentSelected && onAssessmentSelected(option.assessment),
                value: currentSelected,
                options,
                noOptionsMessage: () => 'No Published Assessment'
              }}
            />
            <IconButton
              color="blue"
              mini
              noUi
              light={false}
              data-test={`delete-assessment-${currentSelected?.id}`}
              icon="trash"
              onClick={onUnselectAssessment}
            />
            {currentSelected && <Button
              outline
              data-test="view-simulation-settings-button"
              onClick={() => setShowSettingsPreviewModal(true)}>
              View Assessment
            </Button>}
            {currentSelected && <Button outline>
              <Link to={`/events/${eventId}/view/assessments/${currentSelected.id}`}>
                  Mark assessments
              </Link>
            </Button>
            }
          </InlineGroup>
        )
      default:
        return null
    }
  }, [status, currentSelected, clientId, options, onUnselectAssessment, eventId, onAssessmentSelected])

  return (
    <>
      <VerticalGroup spaceBetweenElements={4}>
        <Text size="sm" bold>Assessments</Text>
        {content}
        {assessmentId &&
          <AssessmentPreviewModal
            assessmentId={assessmentId}
            isOpen={showSettingsPreviewModal}
            onClose={() => setShowSettingsPreviewModal(false)}
          />
        }
      </VerticalGroup>
    </>
  )
}

export default SelectAssessment
